/**
 * Avenged Sevenfold — Life Is But a Dream… · songs.
 *
 * Source of truth: `a7x_nyx_landing_songs.json` (interpretation block per
 * song). Mappings:
 *
 *   - `n`           : track position from the album drop (Game Over=1,
 *                     Nobody=3, Cosmic=5). Not the song-index in the JSON.
 *   - `duration`    : ms / 1000, formatted as M:SS, rounded to nearest sec.
 *   - `perspective` : flattened from the nested `perspective.description`.
 *   - `voice`       : flattened from `perspective.voice` (the JSON also
 *                     ships `perspective.addressing`, which has no slot in
 *                     our schema and is intentionally dropped — it adds
 *                     editorial flavour the UI doesn't render).
 *   - `alternative` : `alternative_readings[0]` — the JSON ships a single
 *                     reading per song, so the array is collapsed.
 *   - `live`        : `live_tips` renamed; the inner shape is identical
 *                     (mindset / listen_for / line_to_feel).
 *
 * Everything else (summary, themes, tone, symbols, key_lines) is forwarded
 * verbatim to keep the published analysis untouched.
 */

import type { SongMeaning } from '@/types/album';

export const songs: SongMeaning[] = [
  {
    n: 1,
    title: 'Game Over',
    duration: '3:46',
    summary:
      '"Game Over" retrata una vida entera reducida a hitos, costumbres y frases de consumo hasta que el hablante siente que ya no pertenece a nada. La tensión que la atraviesa es cruel: la vida cotidiana promete sentido y continuidad, pero al final le deja una sensación de vacío, actuación y salida irreversible.',
    perspective:
      'La voz suena como alguien que recorre toda su vida a cámara rápida y de pronto ya no puede sostener la actuación. Primero enumera etapas, hábitos y escenas comunes con una energía casi mecánica. Después se quiebra y habla desde el cansancio, la alienación y una decisión terminal.',
    voice: 'Primera persona fragmentada, acelerada, sarcástica y luego devastadoramente directa.',
    themes: [
      'vacío existencial dentro de la vida cotidiana',
      'desconexión de uno mismo y actuación social',
      'muerte voluntaria como cierre desesperado',
    ],
    tone: ['frenético y satírico', 'desolado'],
    symbols: [
      {
        quote: 'try a happy face',
        meaning:
          'El "happy face" condensa la presión de fingir normalidad mientras por dentro ya hay derrumbe. La canción ataca esa máscara social de bienestar obligatorio.',
      },
      {
        quote: 'my family tree',
        meaning:
          'El "family tree" carga con linaje, infancia, herencia y pertenencia. Convertirlo en el lugar del ahorcamiento vuelve la despedida más amarga, porque rompe con la idea de que la familia o la biografía bastan para sostener una vida.',
      },
    ],
    key_lines: [
      {
        lyric: 'Days are fine and come on time',
        explanation:
          'Esta línea concentra la tesis emocional. El paso del tiempo deja de sentirse neutral y se vuelve una trampa: los días pasan ordenados, pero los años vacían a la persona. Ahí aparece el miedo central de la canción, vivir mucho y encontrarse cada vez menos.',
      },
      {
        lyric: 'And here I swing from my family tree',
        explanation:
          'Este remate destroza cualquier consuelo fácil. El gesto es brutal y teatral a la vez: el árbol familiar, que suele sugerir origen, herencia y continuidad, queda convertido en escenario de ruptura total. Es la imagen que más se te queda pegada.',
      },
    ],
    alternative: {
      label: 'Vida resumida como collage y derrumbe',
      description:
        'Lectura secundaria: más que una confesión lineal, la canción puede oírse como un montaje de toda una vida comprimida hasta volverse ruido. La evidencia está en las listas de palabras sueltas y en el salto brusco desde recuerdos cotidianos al suicidio final. Eso hace que el golpe cierre menos como "historia" y más como vértigo existencial.',
    },
    live: {
      mindset:
        'Una calma incómoda antes del golpe. La canción pide escuchar cómo la rutina, los chistes y los recuerdos se van pudriendo por dentro hasta volverse despedida.',
      listen_for:
        'El cambio desde las listas aceleradas y casi absurdas hacia la confesión directa del final. Cuando entra "It strikes me", la canción deja la sátira nerviosa y cae en algo mucho más desnudo.',
      line_to_feel: "Can't you see life is but a dream anyway?",
    },
  },
  {
    n: 3,
    title: 'Nobody',
    duration: '5:54',
    summary:
      '"Nobody" vive en el choque entre éxtasis espiritual y anulación personal. La canción imagina una conciencia que roza lo absoluto y descubre que ese despertar también arranca el nombre, el cuerpo y la identidad.',
    perspective:
      'La voz entra en una experiencia de disolución del yo. Quiere fundirse con algo total, pero también atraviesa terror, violencia y extrañeza ante su propia desaparición.',
    voice: 'Primera persona, visionaria y fracturada.',
    themes: ['disolución del ego', 'trascendencia con terror'],
    tone: ['extático', 'inquietante'],
    symbols: [
      {
        quote: 'I am the sun',
        meaning:
          'El sol aparece como imagen de totalidad, energía pura y conciencia absoluta. Cuando dice "I am the sun", la voz ya no habla desde una persona concreta, habla desde algo inmenso y desbordado.',
      },
      {
        quote: "I'm a man without a head",
        meaning:
          'La cabeza ausente vuelve literal la pérdida del ego. "I\'m a man without a head" suena a iluminación, decapitación simbólica y desorientación total al mismo tiempo.',
      },
      {
        quote: 'The crack in the darkness',
        meaning:
          'La grieta en la oscuridad y el cielo rasgado marcan el instante de ruptura. Es la apertura hacia otra percepción, pero también el quiebre violento de la realidad conocida.',
      },
    ],
    key_lines: [
      {
        lyric: 'This is "I am all" as "I am none"',
        explanation:
          'Esta línea clava la tensión central. El yo toca una idea de totalidad, pero para llegar ahí tiene que borrarse. Por eso la canción pega tanto: la iluminación suena enorme y al mismo tiempo da miedo.',
      },
      {
        lyric: 'Nobody',
        explanation:
          'Es el golpe emocional y el mantra del tema. Después de toda la imaginería cósmica, termina en una palabra vacía y brutal. La multitud la corea porque resume esa sensación de disolverse hasta perder nombre, rostro y centro.',
      },
    ],
    alternative: {
      label: 'Último aliento y desprendimiento del cuerpo',
      description:
        'Lectura de muerte física y visión final. Líneas como "I walk with death in final exhalation" y "I leave a stranger laying lifeless" dejan oír una escena de salida del cuerpo, donde la expansión espiritual también puede sentirse como el último segundo antes de morir.',
    },
    live: {
      mindset: 'Una calma incómoda frente a la idea de desaparecer. La canción pide entrega antes que entendimiento.',
      listen_for:
        'El momento en que la canción pasa de la expansión cósmica al vacío desnudo del estribillo. Ahí la grandeza se convierte en vértigo.',
      line_to_feel: 'Nobody',
    },
  },
  {
    n: 5,
    title: 'Cosmic',
    duration: '7:31',
    summary:
      '"Cosmic" duele porque junta dos impulsos que nunca terminan de acomodarse: aceptar que algo se fue y seguir hablándole como si el lazo todavía cruzara vidas, eras y galaxias. La canción no cierra esa tensión. Vive ahí, entre el adiós y la fe de que el amor deja rastros en todas partes.',
    perspective:
      'La voz canta desde una mezcla de pérdida, fidelidad y vértigo cósmico. Quiere creer que el amor sobrevive al tiempo y a la desaparición física, pero sabe que cada intento de retenerlo también duele.',
    voice: 'Primera persona íntima, con tono de promesa y despedida.',
    themes: [
      'amor que persiste más allá de una vida',
      'duelo convertido en promesa',
      'tiempo cósmico frente a fragilidad humana',
    ],
    tone: ['dolido', 'trascendente', 'devoto'],
    symbols: [
      {
        quote: 'the stars beyond forever',
        meaning:
          'Las estrellas, el polvo y el hielo vuelven el amor algo más grande que una biografía. La búsqueda ya no ocurre en un lugar humano, sino en un espacio donde el tiempo individual pierde peso.',
      },
      {
        quote: 'writing in the caves',
        meaning:
          'Estas imágenes de distintas épocas humanas empujan la idea de almas que se encuentran una y otra vez. No importa la forma concreta de cada vida. Importa la continuidad del vínculo.',
      },
      {
        quote: 'roses born again',
        meaning:
          'La rosa que renace en el viento mete belleza y resurrección dentro de un contexto de pérdida. La imagen suaviza el duelo sin borrarlo.',
      },
    ],
    key_lines: [
      {
        lyric: 'But not a last goodbye',
        explanation:
          'Esta línea condensa el tirón central de la canción. Hay cierre, pero el vínculo sigue abierto. Por eso pega tanto: acepta la pérdida sin soltar del todo a la otra persona.',
      },
      {
        lyric: "I'll follow you",
        explanation:
          'Este es el gran ancla emocional. La voz promete una búsqueda total, más allá del tiempo, del cuerpo y de una sola vida. Es la clase de línea que une a la gente porque suena a juramento.',
      },
      {
        lyric: 'Pain, it found its way back in',
        explanation:
          'Acá aparece la herida sin adornos. Después de hablar de amor en muchas vidas, la canción admite que el dolor siempre regresa. Le da peso trágico a toda la visión cósmica.',
      },
    ],
    alternative: {
      label: 'Duelo y reencuentro después de la muerte',
      description:
        'Lectura secundaria: la canción también puede oírse como una despedida a alguien muerto, con la promesa de reencontrarse en cualquier forma del universo. La sostiene la repetición de "until we meet again" y ese "I\'m gone" final, que vuelve la idea de reencarnación mucho más dolorosa y concreta.',
    },
    live: {
      mindset: 'Duelo colectivo sin solemnidad. La canción pide entrega y asombro al mismo tiempo.',
      listen_for:
        'El salto entre la estrofa herida y el estribillo suspendido. Cuando entra "There you\'ll find me", la canción deja el duelo terrestre y se abre a algo inmenso.',
      line_to_feel: "I'll follow you",
    },
  },
];
